import React from "react";
import { useHistory, Route, useRouteMatch } from "react-router-dom";
import NoSidebarLayout from "../../components/Layout/NoSidebarLayout";

import { useStore } from "../../store/useStore";
import { Cache } from "aws-amplify";
import { Form, Input, Button, notification } from "antd";
import { resetPasswordRequest, resetPassword } from "../../store/actions";
import { MailOutlined } from "@ant-design/icons";
import Text from "antd/lib/typography/Text";

type EmailFormData = {
  email: string;
};

type ResetFormData = {
  code: string;
  password: string;
  confirmPassword: string;
};

const ForgotPasswordPage: React.FC = () => {
  const { dispatch } = useStore();
  const { path } = useRouteMatch();
  const history = useHistory();

  const handleEmailSubmission = async (values: EmailFormData) => {
    const { email } = values;
    try {
      await dispatch(resetPasswordRequest(email));
      Cache.setItem("resetEmailRequest", email);
      notification.info({
        message: "A one time passcode has been sent, please check your email",
        placement: "bottomRight",
      });
      history.push(`${path}/reset`);
    } catch (error) {
      switch (error.code) {
        case "UserNotFoundException": {
          notification.error({
            message: "No account found with that email",
            placement: "bottomRight",
          });
          break;
        }
        case "InvalidParameterException": {
          notification.error({
            message: error.message,
            placement: "bottomRight",
          });
          break;
        }
        default: {
          notification.error({
            message: "An unknown error ocurred, please try again later",
            placement: "bottomRight",
          })
          break;
        }
      }
    }
  };

  const handleNewPassword = async (values: ResetFormData) => {
    const { code, password } = values;
    const email = Cache.getItem("resetEmailRequest");
    try {
      await dispatch(resetPassword(email, code, password));
      Cache.removeItem("resetEmailRequest");
      notification.info({
        message: "Your password has been reset, you can now log in",
        placement: "bottomRight",
      });
      history.push(`/login`);
    } catch (error) {
      switch (error.code) {
        case "UserNotFoundException":
          notification.error({
            message: "No account found with that email",
            placement: "bottomRight",
          });
       }
    }
  };

  return (
    <NoSidebarLayout>
      <div className="flex h-full items-center">
        <Route exact path='/forgot-password'>
          <Form
            name="normal_login"
            className="flex flex-col w-full"
            initialValues={{}}
            onFinish={(values) => {
              handleEmailSubmission(values as EmailFormData);
            }}
          >
            <Text className="text-center mb-4">
              We will send a one time passcode to the account associated with
              this email, which you can use to reset your password
            </Text>

            <Form.Item
              name="email"
              rules={[{ required: true, message: "Please input your email!" }]}
              required
            >
              <Input
                prefix={
                  <div className="flex h-full">
                    <MailOutlined className="self-center" />
                  </div>
                }
                placeholder="Email"
              />
            </Form.Item>
            <Form.Item className="flex flex-col">
              <div className="flex flex-col text-center">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="login-form-button"
                >
                  Reset password
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Route>
        <Route exact path='/forgot-password/reset'>
          <Form
            name="normal_login"
            className="flex flex-col w-full"
            initialValues={{}}
            onFinish={(values) => {
              handleNewPassword(values as ResetFormData);
            }}
          >
            <Form.Item
              name="code"
              rules={[
                { required: true, message: "Required" },
                { min: 6, max: 6, message: "Code must be 6 digits" },
              ]}
            >
              <Input
                className="text-center"
                autoComplete="new-password"
                placeholder="6 digit code"
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: "Please input a password",
                },
                { min: 6, message: "Password must be at least 6 characters" },
              ]}
            >
              <Input.Password
                autoComplete="new-password"
                placeholder="Password"
              />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              rules={[
                {
                  required: true,
                  message: "Please confirm your password",
                },
                ({ getFieldValue }) => ({
                  validator(rule, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }

                    return Promise.reject(
                      "The two passwords that you entered do not match"
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                autoComplete="new-password"
                placeholder="Confirm password"
              />
            </Form.Item>
            <Form.Item className="flex flex-col">
              <div className="flex flex-col text-center">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="login-form-button"
                >
                  Reset password
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Route>
      </div>
    </NoSidebarLayout>
  );
};

export default ForgotPasswordPage;
